import { useLayoutEffect, useRef, type FocusEvent } from "react";
import { Search } from "lucide-react";
import { en, type Translations } from "../../i18n/en";
import { useLanguage } from "../../i18n/language-provider";
import { isApplePlatform, shortcutLabel } from "../../hooks/useCommandPaletteShortcut";
import { useCompetitorTracking } from "../../hooks/useCompetitorTracking";
import { handFocusBack, isHandingFocusBack } from "../../navigation/focusHandBack";
import { navigate } from "../../navigation/history";
import { buildPaletteEntries, paletteFilter, type PaletteAction } from "../../navigation/paletteEntries";
import type { AppRoute } from "../../navigation/routes";
import { useShownCalendarView } from "../../navigation/shownCalendarView";
import type { UserRole } from "../../types/config";
import { useFrontDeskAccess, useViewerAccess } from "../access/read-only-access";
import { usePropertyOpenElsewhere } from "../pms/property-open-elsewhere";
import { useInsidePhoneMenu } from "../RailFrame";
import { currentPricesTab, openCalendarView, openPricesTab } from "../rail/prices-places";
import { Button } from "../ui/button";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

type PaletteCopy = Translations["commandPalette"];

/** The palette's words, defaulted one by one from English: the harnesses hand partial tables. */
function commandPaletteCopy(t: { commandPalette?: Partial<PaletteCopy> }): PaletteCopy {
    return { ...en.commandPalette, ...t.commandPalette };
}

/** A focus a closing layer hands back is not a reader's look at the control. */
function keepTooltipShut(event: FocusEvent<HTMLElement>) {
    if (isHandingFocusBack()) event.preventDefault();
}

export interface CommandPaletteProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The shell's move, which checks the page is open to the reader. */
    onNavigate: (route: AppRoute) => void;
    isPageAllowed: (route: AppRoute) => boolean | "pending";
    userRole?: UserRole | null;
    /** The rail locks Strategies, Prices and the analytics until the property analysis is in. */
    analysisRequired: boolean;
    /** The route on screen, which decides whether a view changes in place. */
    currentRoute: AppRoute;
    activeConnectionId: number | null | undefined;
    connectionVersion: number;
}

/** The quick jump to a page, a Prices tab or a calendar view, opened by ⌘K / Ctrl+K or from the rail. */
export function CommandPalette({
    open,
    onOpenChange,
    onNavigate,
    isPageAllowed,
    userRole,
    analysisRequired,
    currentRoute,
    activeConnectionId,
    connectionVersion,
}: CommandPaletteProps) {
    const { t } = useLanguage();
    const copy = commandPaletteCopy(t);
    const frontDesk = useFrontDeskAccess();
    const viewer = useViewerAccess();
    const openElsewhere = usePropertyOpenElsewhere();
    const published = useShownCalendarView();
    const tracking = useCompetitorTracking(activeConnectionId, connectionVersion, {
        read: open && !frontDesk && !openElsewhere && !analysisRequired && activeConnectionId != null,
    });

    // Radix hands the focus to a trigger as a dialog shuts, and a palette opened by a shortcut has none.
    const returnFocus = useRef<HTMLElement | null>(null);
    useLayoutEffect(() => {
        // Runs before the dialog's content mounts and takes the focus.
        if (open) returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }, [open]);

    const groups = open
        ? buildPaletteEntries({ t, isPageAllowed, userRole, analysisRequired, frontDesk, viewer, tracking })
        : [];
    const shownView = currentPricesTab(currentRoute) === "calendar" ? published : null;

    const select = (action: PaletteAction) => {
        onOpenChange(false);
        if (action.kind === "view") {
            openCalendarView(action.view, { route: currentRoute, navigateTo: onNavigate, navigate, shownView });
        } else if (action.route.page === "drafts" && action.route.tab) {
            openPricesTab(action.route.tab, { route: currentRoute, navigateTo: onNavigate });
        } else {
            onNavigate(action.route);
        }
    };

    return (
        <CommandDialog
            open={open}
            onOpenChange={onOpenChange}
            title={copy.title}
            description={copy.description}
            commandProps={{ filter: paletteFilter, label: copy.placeholder }}
            onCloseAutoFocus={(event) => {
                const target = returnFocus.current;
                returnFocus.current = null;
                if (!target?.isConnected) return;
                event.preventDefault();
                handFocusBack(() => target.focus({ preventScroll: true }));
            }}
        >
            {/* The dialog's Close button stands over the input's end. */}
            <CommandInput placeholder={copy.placeholder} className="pr-8" />
            <CommandList label={copy.results}>
                <CommandEmpty>{copy.empty}</CommandEmpty>
                {groups.map((group) => (
                    <CommandGroup key={group.key} heading={group.heading}>
                        {group.entries.map((entry) => (
                            <CommandItem
                                key={entry.id}
                                value={entry.id}
                                keywords={[entry.label, ...entry.keywords]}
                                onSelect={() => select(entry.action)}
                            >
                                {entry.icon ? <entry.icon aria-hidden className="size-5" /> : <span aria-hidden className="size-5 shrink-0" />}
                                <span className="truncate">{entry.label}</span>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                ))}
            </CommandList>
        </CommandDialog>
    );
}

interface CommandPaletteTriggerProps {
    /** `header`: an icon in the open rail's header. `rail`: a destination-sized row of the folded rail. */
    placement: "header" | "rail";
    onOpen: () => void;
}

/** Opens the palette from the rail beside the page; the phone's menu is the phone's way and draws none. */
export function CommandPaletteTrigger({ placement, onOpen }: CommandPaletteTriggerProps) {
    const { t } = useLanguage();
    const insidePhoneMenu = useInsidePhoneMenu();
    if (insidePhoneMenu) return null;
    const copy = commandPaletteCopy(t);

    return (
        <Tooltip>
            <TooltipTrigger asChild onFocus={keepTooltipShut}>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onOpen}
                    aria-label={copy.open}
                    aria-haspopup="dialog"
                    aria-keyshortcuts={isApplePlatform() ? "Meta+K" : "Control+K"}
                    className={
                        placement === "header"
                            ? "hidden md:inline-flex shrink-0 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                            : "w-full h-10 sm:h-9 justify-center px-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    }
                >
                    <Search className="h-4 w-4" />
                </Button>
            </TooltipTrigger>
            {/* Beside the header's icon stands the fold toggle, which a tooltip to its right would cover. */}
            <TooltipContent side={placement === "header" ? "bottom" : "right"}>{`${copy.open} · ${shortcutLabel()}`}</TooltipContent>
        </Tooltip>
    );
}
